import React from "react";
import { cn } from "@/lib/utils";

type BaseInput = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
  helperText?: string;
  error?: string | boolean;
  size?: "sm" | "md" | "lg";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  inputClassName?: string;
  labelClassName?: string;
  helperClassName?: string;
  wrapperClassName?: string;
};

export type InputProps = (BaseInput & { label: string }) | (BaseInput & { "aria-label": string });

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      helperText,
      error = false,
      size = "md",
      leftIcon,
      rightIcon,
      className,
      inputClassName,
      labelClassName,
      helperClassName,
      wrapperClassName,
      ...props
    },
    ref
  ) => {
    const sizeClasses: Record<NonNullable<InputProps["size"]>, string> = {
      sm: "h-9 px-3 text-sm font-medium",
      md: "h-11 px-4 text-sm md:text-base font-medium",
      lg: "h-12 px-4 text-lg font-medium",
    };

    const iconPaddingBySize: Record<NonNullable<InputProps["size"]>, { left: string; right: string }> = {
      sm: { left: "pl-9", right: "pr-9" },
      md: { left: "pl-11", right: "pr-11" },
      lg: { left: "pl-12", right: "pr-12" },
    };

    const hasLeftIcon = Boolean(leftIcon);
    const hasRightIcon = Boolean(rightIcon);

    const generatedId = React.useId();
    const inputId = id ?? props.name ?? generatedId;
    const describedById = helperText || error ? `${inputId}-desc` : undefined;

    const labelText = "label" in props ? props.label : undefined;
    // Remove label from props to prevent it from being spread to the input element
    const { label: _, ...inputProps } = "label" in props ? props : { ...props, label: undefined };

    return (
      <div className={cn("w-full", wrapperClassName)}>
        {labelText ? (
          <label htmlFor={inputId} className={cn("block mb-1.5 text-sm font-medium text-text-primary", labelClassName)}>
            {labelText}
          </label>
        ) : null}

        <div className={cn("relative", className)}>
          {hasLeftIcon ? (
            <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500")}>{leftIcon}</span>
          ) : null}

          <input
            id={inputId}
            ref={ref}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedById}
            aria-label={labelText}
            className={cn(
              "block w-full rounded-md border border-gray-300 bg-white text-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition",
              sizeClasses[size],
              hasLeftIcon && iconPaddingBySize[size].left,
              hasRightIcon && iconPaddingBySize[size].right,
              Boolean(error) && "border-red-500 focus:border-red-500 focus:ring-red-500/20",
              inputClassName
            )}
            {...inputProps}
          />

          {hasRightIcon ? (
            <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500")}>{rightIcon}</span>
          ) : null}
        </div>

        {helperText && !error ? (
          <p id={describedById} className={cn("mt-1 text-xs text-gray-500", helperClassName)}>
            {helperText}
          </p>
        ) : null}
        {error ? (
          <p id={describedById} className={cn("mt-1 text-xs text-primary", helperClassName)}>
            {typeof error === "string" ? error : "Invalid value"}
          </p>
        ) : null}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };


